/**
 * FirebaseUserService - User profile service for hosted mode
 *
 * Wraps the existing UserProfileAPI to implement IUserProfileService interface.
 * Used in hosted mode when Firebase credentials are provided.
 */

import type { FirebaseApp } from 'firebase/app';
import type {
  IUserProfileService,
  UserProfileData,
  FeatureKey,
} from '../interfaces';
import { Logger } from '../../utils/Logger';

const logger = Logger.getLogger('FirebaseUserService');
import {
  getUserProfile as getFirebaseUserProfile,
  hasFeatureAccess as checkFirebaseFeatureAccess,
  type UserProfile,
  type FeatureKey as FirebaseFeatureKey,
} from '../../api/UserProfileAPI';

export class FirebaseUserService implements IUserProfileService {
  /**
   * Create a Firebase user service
   * @param _app Firebase app instance (UserProfileAPI accesses Firestore directly)
   */
  constructor(_app: FirebaseApp) {
    // Note: We don't need to store the app here because
    // UserProfileAPI already has access to Firestore via its own imports
  }

  /**
   * Get user profile from Firestore
   */
  async getUserProfile(userId: string): Promise<UserProfileData | null> {
    try {
      const profile = await getFirebaseUserProfile(userId);

      if (!profile) {
        return null;
      }

      // Map Firebase UserProfile to our UserProfileData interface
      return {
        userId: profile.userId,
        tier: profile.tier,
        analyticsEnabled: profile.analyticsEnabled !== false, // Default to true
      };
    } catch (error) {
      logger.error('Error getting user profile:', error);
      return null;
    }
  }

  /**
   * Check if user has access to a feature based on their tier
   */
  hasFeatureAccess(profile: UserProfileData, feature: FeatureKey): boolean {
    // Create a minimal UserProfile object for the existing API
    // Note: timestamps are not used for feature access checks, so we use a partial cast
    const firebaseProfile = {
      userId: profile.userId,
      tier: profile.tier,
      analyticsEnabled: profile.analyticsEnabled,
    } as UserProfile;

    return checkFirebaseFeatureAccess(firebaseProfile, feature as FirebaseFeatureKey);
  }
}
